/**
 * RouteErrorBoundary (Plan 06-02, Task 1; D-15).
 *
 * React Router v7 route-level error fallback. Attached to every
 * authenticated route in `createBrowserRouter([...])` via the modern
 * capitalised `ErrorBoundary` route property — when a loader or render
 * throws, the router renders this component INSTEAD of the route's
 * normal `element`, keeping the surrounding shell (NavShell, etc.)
 * mounted.
 *
 * Behaviour:
 *   - Reads `useRouteError()` to obtain the thrown value.
 *   - Distinguishes Router `isRouteErrorResponse` (4xx/5xx Response
 *     instances) from arbitrary `Error` / unknown throws.
 *   - Logs a structured `[RouteErrorBoundary]` console.error so a Pi
 *     `journalctl -u stell-web -f` tail surfaces failures (D-15: no
 *     `/api/__internal/client-error` endpoint in v1; T-06-04
 *     Information-Disclosure mitigation — raw message stays in the log
 *     stream, NOT the rendered DOM).
 *   - Renders `<ErrorState variant="page">` with i18n
 *     `errors.generic.{heading,body}` copy + a `Prøv igjen` retry CTA
 *     that triggers `window.location.reload()` (the simplest
 *     full-state-reset on a route-level crash).
 *
 * Norwegian copy: comes from `errors.generic.*` — never hard-coded
 * here, because we still have a working i18n context at this level
 * (i18n itself crashing is the `AppErrorBoundary`'s job to catch).
 */
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError, isRouteErrorResponse } from 'react-router';
import { ErrorState } from '../primitives';

export function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();
  const { t } = useTranslation();

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'unknown';
  // Structured log only — T-06-04: raw error.message NEVER leaks to DOM.
  console.error('[RouteErrorBoundary]', message, error);

  return (
    <ErrorState
      variant="page"
      heading={t('errors.generic.heading')}
      body={t('errors.generic.body')}
      onRetry={() => window.location.reload()}
    />
  );
}
